import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createOrganization } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { getErrorMessage } from "@/lib/utils";
import { Buildings, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function OrgSetupPage() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [form, setForm] = useState({ name: "", abn: "", phone: "", address: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Organization name is required"); return; }
    setLoading(true);
    try {
      await createOrganization(form);
      toast.success("Organization created! 7-day free trial started.");
      await checkAuth();
      navigate("/dashboard");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to create organization"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-black flex items-center justify-center mx-auto mb-4">
            <Buildings weight="duotone" className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold font-heading text-slate-900" data-testid="org-setup-title">Set Up Your Business</h1>
          <p className="text-slate-500 mt-2">Create your organization to get started with a 7-day free trial</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl p-8 space-y-5 shadow-sm">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Business Name *</label>
            <input data-testid="org-name" type="text" placeholder="e.g. Sydney Car Rentals" value={form.name}
              onChange={e => setForm(p => ({...p, name: e.target.value}))}
              className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">ABN (optional)</label>
            <input data-testid="org-abn" type="text" placeholder="XX XXX XXX XXX" value={form.abn}
              onChange={e => setForm(p => ({...p, abn: e.target.value}))}
              className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Phone (optional)</label>
            <input data-testid="org-phone" type="text" placeholder="+61 XXX XXX XXX" value={form.phone}
              onChange={e => setForm(p => ({...p, phone: e.target.value}))}
              className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Address (optional)</label>
            <input data-testid="org-address" type="text" placeholder="123 Main St, Sydney NSW 2000" value={form.address}
              onChange={e => setForm(p => ({...p, address: e.target.value}))}
              className="w-full h-12 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black" />
          </div>
          <button data-testid="org-submit" type="submit" disabled={loading}
            className="w-full h-12 bg-primary text-primary-foreground rounded-md font-bold text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
            {loading ? "Creating..." : <><span>Start Free Trial</span><ArrowRight weight="bold" className="w-4 h-4" /></>}
          </button>
          <p className="text-xs text-slate-400 text-center">7 days free, then $49/month AUD. Cancel anytime.</p>
        </form>
      </div>
    </div>
  );
}
